import { type PropsWithChildren, useMemo } from 'react';

import { type AnyContext, AuthContext, type LoggedInContext } from '../AuthContext/index.js';
import * as api from '../api.js';
import { useDeprecatedAsync } from '../hooks/useAsync.js';

function getLoggedIn(this: AnyContext): LoggedInContext {
  if (this.status !== 'resolved' || !this.user) {
    throw new Error('Not logged in!');
  }
  if (!this.user.registered) {
    throw new Error('Not registered!');
  }
  return this;
}

export function AuthManager(props: PropsWithChildren) {
  const [data, error, status, refreshLoginCheck] = useDeprecatedAsync(async () => {
    const loggedIn = await api.client.User.isLoggedIn();
    if (!loggedIn) {
      return { user: undefined, permissions: [], featureFlags: [], allUserRebacPermissions: {} };
    }

    const [user, permissions, featureFlags, allUserRebacPermissions] = await Promise.all([
      api.client.User.me(),
      api.client.UserGroup.myPermissions(),
      api.client.FeatureFlag.me(),
      api.client.FlowPermissions.fetchUserDirectPermissions(),
    ]);

    return { user, permissions, featureFlags, allUserRebacPermissions };
  }, []);

  const contextValue = useMemo<AnyContext>((): AnyContext => {
    if (status === 'pending') {
      return {
        status: 'pending',
        getLoggedIn,
        permissions: [],
        featureFlags: [],
        allUserRebacPermissions: {},
        refreshLoginCheck,
      };
    } else if (status === 'rejected') {
      return {
        status: 'rejected',
        error,
        getLoggedIn,
        permissions: [],
        featureFlags: [],
        allUserRebacPermissions: {},
        refreshLoginCheck,
      };
    } else {
      return {
        status: 'resolved',
        getLoggedIn,
        async logout() {
          await api.client.User.logout();
          refreshLoginCheck();
        },
        refreshLoginCheck,
        ...data,
      };
    }
  }, [data, error, status, refreshLoginCheck]);

  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>;
}
